import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: { message: "Missing session id" } });
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async (n) => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setAttempts(n);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (n >= MAX_POLLS) {
          setState({ status: "timeout", data });
          return;
        }
        timer = setTimeout(() => poll(n + 1), POLL_INTERVAL);
      } catch {
        if (!cancelled) setState({ status: "error", data: null });
      }
    };
    poll(1);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-6 py-32 text-center">
      {state.status === "polling" && (
        <>
          <Loader2 className="text-[#E8A6B8] mx-auto animate-spin" size={36} />
          <h1 className="font-serif-display text-4xl mt-6 text-[#3A3A3A]">
            Confirming your payment…
          </h1>
          <p className="mt-3 text-[#5C5C5C] text-sm">Attempt {attempts}/{MAX_POLLS}</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="text-[#E8A6B8] mx-auto" size={48} strokeWidth={1.5} />
          <h1 className="font-serif-display text-5xl mt-6 text-[#3A3A3A]">
            Welcome to <span className="italic text-[#E8A6B8]">{state.data.program_title || "your program"}</span>
          </h1>
          <p className="mt-4 text-[#5C5C5C]">
            Your payment was successful. Maya will email your program details
            and onboarding next steps within 24 hours.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/programs" className="btn-outline">Explore More</Link>
            <Link to="/" className="btn-pink">Back to Home</Link>
          </div>
        </>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <>
          <XCircle className="text-[#3A3A3A] mx-auto" size={48} strokeWidth={1.5} />
          <h1 className="font-serif-display text-4xl mt-6 text-[#3A3A3A]">
            Hmm, we couldn't confirm your payment.
          </h1>
          <p className="mt-3 text-[#5C5C5C]">
            If your card was charged, please contact us and we'll sort it
            right away.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/programs" className="btn-outline">Back to Programs</Link>
            <Link to="/contact" className="btn-pink">Contact Maya</Link>
          </div>
        </>
      )}
    </div>
  );
}
